import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import type { Country, CountryStatus } from "../../types/country";

interface CountryModalProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit" | "delete";
  country?: Country | null;
  onSave: (countryData: Record<string, unknown>) => Promise<void>;
}

export const CountryModal: React.FC<CountryModalProps> = ({
  open,
  onClose,
  mode,
  country,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    country_code: "",
    country_name: "",
    status: "active" as CountryStatus,
    geom: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (mode === "create") {
        setFormData({
          country_code: "",
          country_name: "",
          status: "active",
          geom: "",
        });
      } else if (country) {
        setFormData({
          country_code: country.country_code,
          country_name: country.country_name,
          status:
            country.status === "inactive" || country.status === "deleted"
              ? country.status
              : "active",
          geom: country.geom || "",
        });
      }
      setError("");
    }
  }, [open, mode, country]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (mode !== "delete") {
      if (!/^[A-Z]{2}$/.test(formData.country_code.trim().toUpperCase())) {
        setError("Country code must be 2 uppercase letters");
        return;
      }
      if (!formData.country_name.trim()) {
        setError("Country name is required");
        return;
      }
    }
    setLoading(true);
    try {
      if (mode === "delete") {
        await onSave({});
      } else if (mode === "edit") {
        await onSave({
          country_name: formData.country_name.trim(),
          status: formData.status === "deleted" ? "inactive" : formData.status,
          geom: formData.geom.trim() || null,
        });
      } else {
        await onSave({
          country_code: formData.country_code.trim().toUpperCase(),
          country_name: formData.country_name.trim(),
          status: formData.status,
          geom: formData.geom.trim() || null,
        });
      }
      onClose();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        />
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>
        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {mode === "create"
                  ? "Add Country"
                  : mode === "edit"
                  ? "Edit Country"
                  : "Delete Country"}
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                {error}
              </div>
            )}

            {mode === "delete" ? (
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100 sm:mx-0">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div className="mt-2 sm:mt-0 sm:ml-4 text-sm text-gray-600">
                  Delete country <strong>{country?.country_name}</strong> (
                  {country?.country_code})?
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Country Code
                  </label>
                  <input
                    value={formData.country_code}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        country_code: e.target.value.toUpperCase(),
                      }))
                    }
                    disabled={mode === "edit"}
                    maxLength={2}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                    placeholder="DK"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Country Name
                  </label>
                  <input
                    value={formData.country_name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, country_name: e.target.value }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Denmark"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    value={formData.status === "deleted" ? "inactive" : formData.status}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        status: e.target.value as CountryStatus,
                      }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Geometry (WKT or GeoJSON)
                  </label>
                  <textarea
                    value={formData.geom}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, geom: e.target.value }))
                    }
                    rows={4}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
                    placeholder="Optional"
                  />
                </div>
              </form>
            )}
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`inline-flex justify-center py-2 px-4 text-sm font-medium rounded-md text-white ${
                mode === "delete"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-blue-600 hover:bg-blue-700"
              } disabled:opacity-50`}
            >
              {loading
                ? "Processing..."
                : mode === "create"
                ? "Add Country"
                : mode === "edit"
                ? "Save Changes"
                : "Delete Country"}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="mt-3 sm:mt-0 sm:mr-3 inline-flex justify-center py-2 px-4 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
